import {useEffect, useState, useRef} from 'react';
import PromotionalVideo from './PromotionalVideo/PromotionalVideo';
import PromoGroup from './PromoGroup/PromoGroup';
import {PromoGroup as _PromoGroup} from '../../models/PromoGroup';
import {Video as _Video} from '../../models/Video'
import Viewport from '../../helpers/Viewport';
import './IdleModeManager.css';

function IdleModeManager({client, catalog, playlist, show, style, enableClosePrevention, disableClosePrevention, onClose, getMetadata}) {
    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [showAnimation, setShowAnimation] = useState(false);
    const [itemIndex, setItemIndex] = useState(0); // Index for the playlist items.
    const [slideIndex, setSlideIndex] = useState(0); // Number between 0 and 1. Used to create the trick to only load two elements at a time.
    const [inTransition, setInTransition] = useState(false);``
    const dimensions = Viewport.dimensions;
    const slideDuration = 500;
    const slideWidth = dimensions.width;
    const sortedItems = playlist.items.sort((a, b) => a.priority - b.priority);
    const sortedPlaylist = [];
    sortedItems.forEach((item) => {
        const itemObject = playlist.itemsObjects.find(object => object.id === item.id);
        if (itemObject) sortedPlaylist.push(itemObject);
    });

    useEffect(() => { // Restart the playlist from the start when a change is detected
        setItemIndex(0);
        setSlideIndex(0);
        setInTransition(false);
    }, [playlist]);

    useEffect(() => {
        if (show) {
            addAnimation();
            setShowAnimation(true);
        } else {
            setShowAnimation(false);
        }
    }, [show]);

    useEffect(() => {
        if (!showAnimation) setTimeout(removeAnimation, 1000);
    }, [showAnimation]);

    const addAnimation = () => {
        if (containerRef && containerRef.current) containerRef.current.style.transition = 'top 1s ease';
    };

    const removeAnimation = () => {
        if (containerRef && containerRef.current) containerRef.current.style.transition = 'unset';
    };

    const onSlidePlaybackComplete = () => { // Start slide transition phase.
        setInTransition(true);
    };

    useEffect(() => { // If in transition, slide to the next item.
        if (inTransition) setSlideIndex(1);
        else setSlideIndex(0);
    }, [inTransition]);

    useEffect(() => { // When slide index changes, wait for the slide animation to complete, then update the item index to render the new current video as the first element.
        if (show && inTransition && slideIndex > 0) {
            setTimeout(() => {
                setItemIndex(index => index < sortedPlaylist.length - 1 ? index + 1 : 0);
            }, slideDuration);
        }
    }, [slideIndex]);

    useEffect(() => { // When item index changes, stop the transition phase.
        setInTransition(false);
    }, [itemIndex]);

    const isVideo = (slide) => {
        return slide && slide instanceof _Video;
    };

    const isPromoGroup = (slide) => {
        return slide && slide instanceof _PromoGroup;
    };

    useEffect(() => { // Check if the next slide requires close prevention or not (this effect is not part of the slide flow).
        if (show) {
            const slide = sortedPlaylist[itemIndex];
            if (slide && isPromoGroup(slide) && enableClosePrevention) enableClosePrevention();
            else if (slide && isVideo(slide) && disableClosePrevention) disableClosePrevention();
            else {
                if (disableClosePrevention) disableClosePrevention();
            }
        }
    }, [show, itemIndex]);

    const containerStyle = () => {
        return {
            top: showAnimation ? 0 : '-100vh'
        };
    };

    const wrapperStyle = () => {
        return {
            width: slideWidth * 2,
            transform: `translateX(-${slideWidth * slideIndex}px)`,
            transition: inTransition ? `transform ${slideDuration / 1000}s ease` : 'unset'
        };
    };

    const slideStyle = () => {
        return {
            width: slideWidth
        };
    };

    const renderPromotionalVideo = (video, loop, start) => {
        const id = `${playlist.id}-${video.id}`;
        return (
            <div key={`promotional-video-${id}`} className='idle-mode-manager-slide' style={slideStyle()}>
                <PromotionalVideo
                    playlistId={playlist.id}
                    itemId={video.id}
                    id={id}
                    url={video.url}
                    loop={loop}
                    muted={video.muted}
                    start={start}
                    onPlaybackComplete={onSlidePlaybackComplete}
                    getMetadata={getMetadata}
                />
            </div>
        );
    };

    const renderPromoGroup = (promoGroup, loop, start) => {
        const _promoGroup = promoGroup.clone();
        const id = `${playlist.id}-${promoGroup.id}`;
        return (
            <div key={`promo-group-${id}`} className='idle-mode-manager-slide' style={slideStyle()}>
                <PromoGroup
                    playlistId={playlist.id}
                    itemId={promoGroup.id}
                    id={id}
                    client={client}
                    catalog={catalog}
                    playlist={playlist}
                    promoGroup={_promoGroup}
                    loop={loop}
                    start={start}
                    style={style}
                    onPlaybackComplete={onSlidePlaybackComplete}
                    closeIdleModeManager={onClose}
                    getMetadata={getMetadata}
                />
            </div>
        );
    };

    const renderPlaylist = () => {
        const loop = sortedPlaylist.length === 1;
        const currentItem = sortedPlaylist[itemIndex];
        let slides = [];
        if (loop) slides = [currentItem];
        else {
            const nextIndex = itemIndex < sortedPlaylist.length - 1 ? itemIndex + 1 : 0;
            const nextItem = sortedPlaylist[nextIndex];
            slides = [currentItem, nextItem];
        }
        const elements = [];
        slides.forEach((slide, index) => {
            const start = show && index === 0 && !inTransition;
            if (isVideo(slide)) elements.push(renderPromotionalVideo(slide, loop, start));
            else if (isPromoGroup(slide)) elements.push(renderPromoGroup(slide, loop, start));
        });
        return elements;
    };

    return (
        <div ref={containerRef} className='idle-mode-manager' style={containerStyle()}>
            <div ref={wrapperRef} className='idle-mode-manager-wrapper' style={wrapperStyle()}>
                {renderPlaylist()}
            </div>
        </div>
    );
}

export default IdleModeManager;